import { CalendarCheck, CalendarPlus, Pencil, Trash2, Check } from "lucide-react";
import { clsx } from "clsx";
import { useDrag } from "@use-gesture/react";
import { useEffect, useState } from "react";
import { Task, isTauriEnvironment } from "../lib/tauri";
import { formatDueDate, isOverdue } from "../lib/date";
import { Checkbox } from "./ui/Checkbox";
import { useTaskStore } from "../stores/taskStore";
import { useUiStore } from "../stores/uiStore";
import { haptic } from "../lib/haptics";
import { TaskCalendarDialog } from "./TaskCalendarDialog";
import { api } from "../lib/tauri";

const priority = {
  high: "bg-danger-500 w-1",
  medium: "bg-warning-500 w-[3px]",
  low: "bg-success-500 w-[2px]",
};

const SWIPE_THRESHOLD = 0.35;
const MAX_OFFSET = 0.6;

export function TaskItem({ task }: { task: Task }) {
  const toggleTask = useTaskStore((s) => s.toggleTask);
  const deleteTask = useTaskStore((s) => s.deleteTask);
  const setEditorOpen = useUiStore((s) => s.setEditorOpen);
  const showToast = useUiStore((s) => s.showToast);
  const overdue = isOverdue(task.dueDate, task.completed);

  const [offsetX, setOffsetX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarLinked, setCalendarLinked] = useState(false);
  const canUseGcal = isTauriEnvironment();

  useEffect(() => {
    if (!canUseGcal) return;
    void api.gcalTaskLinkStatus?.(task.id)
      .then((status) => setCalendarLinked(status.linked))
      .catch(() => setCalendarLinked(false));
  }, [canUseGcal, task.id]);

  const bind = useDrag(
    ({ down, movement: [mx], cancel, last, tap }) => {
      if (tap) return;
      const width = window.innerWidth;
      if (down) {
        setIsDragging(true);
        const clamped = Math.max(-width * MAX_OFFSET, Math.min(width * MAX_OFFSET, mx));
        setOffsetX(clamped);
      } else if (last) {
        setIsDragging(false);
        const ratio = Math.abs(mx) / width;
        if (ratio >= SWIPE_THRESHOLD) {
          if (mx > 0) {
            haptic("success");
            void toggleTask(task.id);
          } else {
            haptic("warning");
            void deleteTask(task);
            showToast("削除しました");
          }
          cancel();
        }
        setOffsetX(0);
      }
    },
    {
      axis: "x",
      filterTaps: true,
      pointer: { touch: true },
    }
  );

  const rightActionVisible = offsetX > 12;
  const leftActionVisible = offsetX < -12;

  return (
    <div className="relative overflow-hidden rounded-md">
      {/* Right action background (complete) */}
      <div
        className={clsx(
          "pointer-events-none absolute inset-0 flex items-center justify-start rounded-md bg-success-500 px-6 text-white transition-opacity",
          rightActionVisible ? "opacity-100" : "opacity-0"
        )}
      >
        <Check className="h-6 w-6" />
        <span className="ml-2 text-sm font-semibold">
          {task.completed ? "未完了に戻す" : "完了"}
        </span>
      </div>

      {/* Left action background (delete) */}
      <div
        className={clsx(
          "pointer-events-none absolute inset-0 flex items-center justify-end rounded-md bg-danger-500 px-6 text-white transition-opacity",
          leftActionVisible ? "opacity-100" : "opacity-0"
        )}
      >
        <span className="mr-2 text-sm font-semibold">削除</span>
        <Trash2 className="h-6 w-6" />
      </div>

      {/* Foreground card */}
      <div
        {...bind()}
        className="group relative flex min-h-[52px] items-center gap-3 rounded-md border border-border bg-card px-4 py-3 transition-colors hover:bg-hover hover:shadow-elevated dark:border-border-dark dark:bg-card-dark dark:hover:bg-hover-dark"
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: isDragging ? "none" : "transform 220ms cubic-bezier(0.2, 0.9, 0.3, 1)",
          touchAction: "pan-y",
        }}
      >
        <div className={clsx("self-stretch rounded-full", priority[task.priority], task.completed && "opacity-30")} />
        <Checkbox checked={task.completed} onClick={() => { haptic("light"); void toggleTask(task.id); }} />
        <button className="min-w-0 flex-1 text-left" onClick={() => setEditorOpen(true, task.id)}>
          <h3 className={clsx("truncate text-sm font-medium", task.completed && "text-muted line-through dark:text-muted-dark")}>{task.title}</h3>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-muted dark:text-muted-dark">
            {task.dueDate && <span className={clsx("font-mono font-medium", overdue && "text-danger-600")}>{formatDueDate(task.dueDate)}</span>}
            {task.description && <span className="truncate">{task.description}</span>}
          </div>
        </button>
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          {canUseGcal && (
            <button
              aria-label={calendarLinked ? "カレンダー更新" : "カレンダーに追加"}
              title={calendarLinked ? "カレンダー更新" : "カレンダーに追加"}
              className="rounded p-1.5 text-muted hover:bg-card hover:text-brand-600 dark:hover:bg-card-dark"
              onClick={() => setCalendarOpen(true)}
            >
              {calendarLinked ? <CalendarCheck className="h-4 w-4" /> : <CalendarPlus className="h-4 w-4" />}
            </button>
          )}
          <button aria-label="編集" className="rounded p-1.5 text-muted hover:bg-card hover:text-text dark:hover:bg-card-dark" onClick={() => setEditorOpen(true, task.id)}><Pencil className="h-4 w-4" /></button>
          <button aria-label="削除" className="rounded p-1.5 text-muted hover:bg-card hover:text-danger-500 dark:hover:bg-card-dark" onClick={() => { haptic("warning"); void deleteTask(task); showToast("削除しました"); }}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      {canUseGcal && (
        <TaskCalendarDialog
          task={task}
          open={calendarOpen}
          onClose={() => {
            setCalendarOpen(false);
            void api.gcalTaskLinkStatus?.(task.id)
              .then((status) => setCalendarLinked(status.linked))
              .catch(() => setCalendarLinked(false));
          }}
        />
      )}
    </div>
  );
}
